import {
  Body, Controller, Delete, Get, Param, Patch, Post, Req,
  UploadedFile, UseGuards, UseInterceptors,
} from '@nestjs/common';
import { FileInterceptor } from '@nestjs/platform-express';
import { ApiOperation, ApiTags } from '@nestjs/swagger';
import { AdminAccessGuard } from '../auth/admin-access.guard';
import { AuthGuard, AuthenticatedRequest } from '../auth/auth.guard';
import { DemoModeGuard } from '../auth/demo-mode.guard';
import { StoreContextService } from '../tenancy/store-context.service';
import { BrandingService, LogoRole } from './branding.service';

/**
 * Logos are marks, not photographs — a few hundred KB at most. The cap is here
 * to stop someone posting a 40 MB TIFF at the endpoint, not to be a real
 * budget; the per-plan quota is what actually governs how much a merchant can
 * store.
 */
const LOGO_MAX_BYTES = 5 * 1024 * 1024;

/**
 * Branding and SEO, in two tiers.
 *
 * The merchant routes are the business — one logo, one set of social profiles,
 * inherited by every store. The store routes are a shopfront, where search SEO
 * and the local block live because a store is what has a URL.
 */
@ApiTags('branding')
@Controller('admin')
@UseGuards(AuthGuard, AdminAccessGuard, DemoModeGuard)
export class BrandingController {
  constructor(
    private readonly branding: BrandingService,
    private readonly storeContext: StoreContextService
  ) {}

  private orgId(): string {
    return this.storeContext.organizationId();
  }

  private actor(req: AuthenticatedRequest): string {
    return req.user?.id ?? 'system';
  }

  // --- Merchant tier --------------------------------------------------------

  @Get('merchant/branding')
  @ApiOperation({ summary: 'Merchant brand: logos, social, business identity' })
  merchantBranding() {
    return this.branding.getMerchantBranding(this.orgId());
  }

  @Patch('merchant/branding')
  @ApiOperation({ summary: 'Update the merchant brand' })
  saveMerchantBranding(
    @Body() body: Record<string, unknown>,
    @Req() req: AuthenticatedRequest
  ) {
    return this.branding.saveMerchantBranding(this.orgId(), body, this.actor(req));
  }

  // --- Store tier -----------------------------------------------------------

  @Get('stores/:storeId/branding')
  @ApiOperation({ summary: 'One store’s branding and local SEO' })
  storeBranding(@Param('storeId') storeId: string) {
    return this.branding.getStoreBranding(storeId);
  }

  @Patch('stores/:storeId/branding')
  @ApiOperation({ summary: 'Update a store’s branding and local SEO' })
  saveStoreBranding(
    @Param('storeId') storeId: string,
    @Body() body: Record<string, unknown>,
    @Req() req: AuthenticatedRequest
  ) {
    return this.branding.saveStoreBranding(storeId, body, this.actor(req));
  }

  /**
   * What the store actually renders with, after the merchant fallback — and
   * which fields came from the merchant, so the form can grey them.
   */
  @Get('stores/:storeId/branding/resolved')
  @ApiOperation({ summary: 'Effective branding, with inheritance applied' })
  resolved(@Param('storeId') storeId: string) {
    return this.branding.resolveForStore(storeId);
  }

  // --- Logos ----------------------------------------------------------------

  @Post('merchant/branding/logo/:role')
  @ApiOperation({ summary: 'Upload a business logo, favicon or share image' })
  @UseInterceptors(FileInterceptor('file', { limits: { fileSize: LOGO_MAX_BYTES } }))
  uploadMerchantLogo(
    @Param('role') role: LogoRole,
    @UploadedFile() file: Express.Multer.File,
    @Req() req: AuthenticatedRequest
  ) {
    return this.branding.uploadLogo({
      organizationId: this.orgId(),
      storeId: null,
      role,
      file,
      actorId: this.actor(req),
    });
  }

  @Delete('merchant/branding/logo/:role')
  @ApiOperation({ summary: 'Clear a business logo slot' })
  clearMerchantLogo(@Param('role') role: LogoRole, @Req() req: AuthenticatedRequest) {
    return this.branding.clearLogo(this.orgId(), null, role, this.actor(req));
  }

  @Get('merchant/branding/logos')
  @ApiOperation({ summary: 'Public URLs for the business logo slots' })
  merchantLogoUrls() {
    return this.branding.logoUrls(this.orgId(), null);
  }

  @Post('stores/:storeId/branding/logo/:role')
  @ApiOperation({ summary: 'Upload a logo for one store' })
  @UseInterceptors(FileInterceptor('file', { limits: { fileSize: LOGO_MAX_BYTES } }))
  uploadStoreLogo(
    @Param('storeId') storeId: string,
    @Param('role') role: LogoRole,
    @UploadedFile() file: Express.Multer.File,
    @Req() req: AuthenticatedRequest
  ) {
    return this.branding.uploadLogo({
      organizationId: this.orgId(),
      storeId,
      role,
      file,
      actorId: this.actor(req),
    });
  }

  @Delete('stores/:storeId/branding/logo/:role')
  @ApiOperation({ summary: 'Clear a store logo slot' })
  clearStoreLogo(
    @Param('storeId') storeId: string,
    @Param('role') role: LogoRole,
    @Req() req: AuthenticatedRequest
  ) {
    return this.branding.clearLogo(this.orgId(), storeId, role, this.actor(req));
  }

  @Get('stores/:storeId/branding/logos')
  @ApiOperation({ summary: 'Public URLs for one store’s logo slots' })
  storeLogoUrls(@Param('storeId') storeId: string) {
    return this.branding.logoUrls(this.orgId(), storeId);
  }

  @Get('stores/:storeId/branding/completeness')
  @ApiOperation({ summary: 'SEO completeness score and what is missing' })
  completeness(@Param('storeId') storeId: string) {
    return this.branding.completeness(storeId);
  }
}
